// Schémas Zod du module « Devis ».
//
// Un devis a un client (optionnel à l'état brouillon), des dates, un statut, une remise et
// des frais de port (en €), et des lignes. Une ligne vient d'un article (articleId) ou est
// libre (articleId absent). Les totaux sont calculés CÔTÉ SERVEUR (jamais reçus du client).
// Franchise de base TVA (293 B) : total HT = total TTC.
//
// Rappel leçon : les champs optionnels pouvant arriver à `null` (formulaire) sont
// `.nullable().optional()`.

import { z } from 'zod'

export const DEVIS_STATUTS = ['brouillon', 'envoye', 'accepte', 'refuse', 'expire'] as const
export type DevisStatut = (typeof DEVIS_STATUTS)[number]

export const LIBELLES_STATUT_DEVIS: Record<DevisStatut, string> = {
  brouillon: 'Brouillon',
  envoye: 'Envoyé',
  accepte: 'Accepté',
  refuse: 'Refusé',
  expire: 'Expiré',
}

// Unité d'AFFICHAGE par ligne (brique 5D). Purement cosmétique : reportée telle quelle sur le
// PDF, SANS aucune conversion ni impact sur le stock. Vide par défaut (même depuis le catalogue).
// Distincte de l'unité de STOCK d'un article (enum unite_article = piece/g/ml) : ne pas confondre.
export const UNITES_LIGNE = ['piece', 'g', 'kg', 'ml', 'l', 'lot'] as const
export type UniteLigne = (typeof UNITES_LIGNE)[number]

// Libellés présentés dans la liste déroulante de l'éditeur (l'option vide « — » = aucune unité).
export const LIBELLES_UNITE_LIGNE: Record<UniteLigne, string> = {
  piece: 'Pièce',
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'L',
  lot: 'Lot',
}

// Abréviations imprimées après la quantité sur le PDF (ex. « 250 ml », « 2 pc », « 1 lot »).
export const ABREV_UNITE_LIGNE: Record<UniteLigne, string> = {
  piece: 'pc',
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'L',
  lot: 'lot',
}

// Abréviation sûre depuis une valeur libre (string | null) : '' si l'unité est absente ou inconnue.
export function abrevUniteLigne(u: string | null | undefined): string {
  return u && u in ABREV_UNITE_LIGNE ? ABREV_UNITE_LIGNE[u as UniteLigne] : ''
}

const dateISO = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ.')

export const ligneDevisSchema = z.object({
  // Présent si la ligne vient du catalogue ; absent/null pour une ligne libre.
  articleId: z.string().uuid("Identifiant d'article invalide.").nullable().optional(),
  designation: z
    .string()
    .trim()
    .min(1, 'La désignation est obligatoire.')
    .max(300, 'Désignation trop longue (300 caractères max).'),
  quantite: z
    .number()
    .positive('La quantité doit être supérieure à 0.')
    .max(1_000_000, 'Quantité trop grande.'),
  prixUnitaire: z
    .number()
    .min(0, 'Le prix ne peut pas être négatif.')
    .max(1_000_000, 'Prix trop grand.'),
  // Unité d'affichage facultative (brique 5D). Vide = aucune unité imprimée. `null` toléré quand
  // le champ est vidé dans le formulaire -> .nullable().optional() (leçon Zod, juin 2026).
  unite: z.enum(UNITES_LIGNE).nullable().optional(),
  ordre: z.number().int().nullable().optional(),
})
export type LigneDevisInput = z.infer<typeof ligneDevisSchema>

export const devisCreateSchema = z.object({
  clientId: z.string().uuid('Client invalide.').nullable().optional(),
  statut: z.enum(DEVIS_STATUTS).default('brouillon'),
  dateDevis: dateISO,
  dateValidite: dateISO.nullable().optional(),
  remise: z.number().min(0, 'La remise ne peut pas être négative.').max(100, 'La remise en % ne peut pas dépasser 100.').default(0),
  fraisPort: z.number().min(0, 'Les frais de port ne peuvent pas être négatifs.').default(0),
  notes: z.string().trim().max(1000, 'Notes trop longues (1000 caractères max).').nullable().optional(),
  lignes: z.array(ligneDevisSchema).max(200, 'Trop de lignes (200 maximum).').default([]),
})
export type DevisCreateInput = z.infer<typeof devisCreateSchema>

// Mise à jour : mêmes champs, tous optionnels sauf les lignes (remplacées en bloc si fournies).
export const devisUpdateSchema = z.object({
  clientId: z.string().uuid('Client invalide.').nullable().optional(),
  statut: z.enum(DEVIS_STATUTS).optional(),
  dateDevis: dateISO.optional(),
  dateValidite: dateISO.nullable().optional(),
  remise: z.number().min(0).max(100).optional(),
  fraisPort: z.number().min(0).optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  lignes: z.array(ligneDevisSchema).max(200).optional(),
})
export type DevisUpdateInput = z.infer<typeof devisUpdateSchema>

// Calcul des totaux (source unique de vérité, partagée côté front pour l'aperçu).
//   sousTotal   = Σ(quantite × prixUnitaire)
//   remiseMontant = sousTotal × (remise% / 100)   [remise est un POURCENTAGE, 0–100]
//   totalHt     = sousTotal − remiseMontant + fraisPort   (≥ 0)
export function calculerTotaux(
  lignes: { quantite: number; prixUnitaire: number }[],
  remisePct = 0,
  fraisPort = 0,
): { sousTotal: number; remiseMontant: number; totalHt: number } {
  const sousTotal = lignes.reduce((s, l) => s + l.quantite * l.prixUnitaire, 0)
  const pct = Math.min(100, Math.max(0, remisePct)) // borne de sécurité 0–100
  const remiseMontant = (sousTotal * pct) / 100
  const totalHt = Math.max(0, sousTotal - remiseMontant + fraisPort)
  // Arrondi au centime pour éviter les flottants disgracieux.
  const arrondi = (n: number) => Math.round(n * 100) / 100
  return { sousTotal: arrondi(sousTotal), remiseMontant: arrondi(remiseMontant), totalHt: arrondi(totalHt) }
}
